import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import {
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiEye,
  FiHome,
  FiPlus,
  FiSearch,
  FiShield,
  FiTrash2,
  FiUsers,
} from "react-icons/fi";
import { MdGroups, MdPendingActions, MdVerified } from "react-icons/md";
import { RoutesPaths, verifications } from "../Router/constant";
import { getUsers, deleteUserById } from "../Services/userService";
import ConfirmationModal from "../Components/ConfirmationModal";
import { toast } from 'react-toastify';
import Skeleton from '../Components/SkeletonLoader';

export default function Usermanagement() {
  const [loading, setLoading] = useState(true);
  const [allData, setAllData] = useState({});
  const [userData, setUserData] = useState([]);
  const [offset, setOffset] = useState(1);
  const [limit] = useState(10);
  const [search, setSearch] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [isActive] = useState("");
  const [isVerified, setIsVerified] = useState("");
  const [confirmation, setConfirmation] = useState({ show: false, userId: null });

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offset, search, isActive, isVerified]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const response = await getUsers({ offset, limit, search, isActive, isVerified });
      const users = response.data?.data?.users || [];
      const allData = response.data?.data || {};
      setUserData(users);
      setAllData(allData);
      setTotalCount(allData?.count || 0);
    } catch (error) {
      console.error("Error fetching users:", error);
      setUserData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (userId) => {
    setConfirmation({ show: true, userId });
  };

  const handleConfirmDelete = async () => {
    try {
      if (confirmation.userId) {
        const response = await deleteUserById(confirmation.userId);
        if (response.data?.status === true) {
          toast.success('User Deleted Successfully');
          fetchAllData();
          setConfirmation({ show: false, userId: null });
        } else {
          console.error("Delete failed:", response.data?.message || "Unknown error");
        }
      }
    } catch (error) {
      console.error("Error deleting user:", error);
    }
  };

  const handleCancelDelete = () => {
    setConfirmation({ show: false, userId: null });
  };

  const generatePagination = (currentPage, totalPages) => {
    const pages = [];

    if (totalPages <= 4) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 2) {
        pages.push(1, 2, '...', totalPages - 1, totalPages);
      } else if (currentPage <= 4) {
        pages.push(3, 4, '...', totalPages - 1, totalPages);
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 2; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(currentPage, currentPage + 1, '...', totalPages - 1, totalPages);
      }
    }

    return pages;
  };

  const verifiedCount = allData?.verifiedCount || 0;
  const unverifiedCount = allData?.unverifiedCount || 0;
  const totalPages = Math.ceil(totalCount / limit);

  const summaryCards = [
    { label: "Total users", value: verifiedCount + unverifiedCount, tone: "orange", Icon: FiUsers, Art: MdGroups },
    { label: "Un-verified count", value: unverifiedCount, tone: "amber", Icon: FiClock, Art: MdPendingActions },
    { label: "Verified count", value: verifiedCount, tone: "green", Icon: FiShield, Art: MdVerified },
  ];

  return (
    <div className="container-fluid qd-page">
      {/* Breadcrumb + header */}
      <nav className="qd-breadcrumb">
        <NavLink to={RoutesPaths.DASHBOARD} aria-label="Dashboard">
          <FiHome />
        </NavLink>
        <FiChevronRight />
        <NavLink to={RoutesPaths.USERMANAGEMENT}>User management</NavLink>
      </nav>
      <div className="qd-page-header">
        <div>
          <h1 className="qd-title">User management</h1>
          <p className="qd-subtitle">
            Manage your platform users, verify accounts and create orders.
          </p>
        </div>
      </div>

      {/* Summary cards */}
      <div className="qd-summary-grid">
        {summaryCards.map(({ label, value, tone, Icon, Art }) => (
          <div className={`qd-card qd-summary qd-summary-${tone}`} key={label}>
            <div className={`qd-stat-icon qd-tone-${tone}`}>
              <Icon />
            </div>
            <div className="qd-stat-body">
              <p className="qd-stat-label">{label}</p>
              {loading ? (
                <div className="qd-skel" style={{ width: 50, height: 30 }} />
              ) : (
                <h3 className="qd-stat-value">{value}</h3>
              )}
            </div>
            <Art className="qd-summary-art" aria-hidden="true" />
          </div>
        ))}
      </div>

      {/* Filters + Table */}
      <div className="qd-card qd-panel">
        <div className="qd-panel-header">
          <div className="qd-panel-title">
            <span className="qd-panel-icon">
              <FiUsers />
            </span>
            <div>
              <h6>User details</h6>
              <p className="qd-panel-sub">
                View and manage all registered users on the platform.
              </p>
            </div>
          </div>
          <div className="qd-bookings-tools">
            <select
              className="qd-select qd-select-wide"
              value={isVerified}
              onChange={(e) => {
                setIsVerified(e.target.value);
                setOffset(1);
              }}
              aria-label="Filter by verification"
            >
              {verifications.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <label className="qd-search">
              <FiSearch />
              <input
                type="text"
                placeholder="Search user..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setOffset(1);
                }}
              />
            </label>
          </div>
        </div>

        <div className="qd-table-wrap">
          <table className="qd-table qd-table-users">
            <thead>
              <tr>
                <th>Customer name</th>
                <th>Customer email</th>
                <th>Status</th>
                <th>Date</th>
                <th className="qd-th-actions">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(5)].map((_, index) => (
                  <tr key={index}>
                    <td><Skeleton width={100} /></td>
                    <td><Skeleton width={160} /></td>
                    <td><Skeleton width={80} /></td>
                    <td><Skeleton width={80} /></td>
                    <td><Skeleton width={200} height={20} /></td>
                  </tr>
                ))
              ) : userData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="qd-empty">No user found</td>
                </tr>
              ) : (
                userData.map((user) => (
                  <tr key={user._id}>
                    <td><strong className="qd-user-name">{user.fullName}</strong></td>
                    <td className="qd-mono">{user.email}</td>
                    <td>
                      {user.isVerified === true ? (
                        <span className="qd-pill qd-pill-success">
                          <FiCheckCircle /> Verified
                        </span>
                      ) : (
                        <span className="qd-pill qd-pill-warning">
                          <FiClock /> Un-verified
                        </span>
                      )}
                    </td>
                    <td>{new Date(user.createdAt).toLocaleDateString('en-GB')}</td>
                    <td>
                      <div className="qd-actions qd-actions-end">
                        <NavLink
                          to={`${RoutesPaths.USERMANAGEMENTDETAIL}/${user._id}`}
                          className="qd-icon-btn"
                          aria-label="View user"
                          title="View"
                        >
                          <FiEye />
                        </NavLink>
                        <button
                          className="qd-icon-btn qd-icon-btn-danger"
                          onClick={() => handleDeleteClick(user._id)}
                          aria-label="Delete user"
                          title="Delete"
                        >
                          <FiTrash2 />
                        </button>
                        <NavLink
                          to={`${RoutesPaths.ADDORDER}/${user._id}`}
                          className="qd-btn qd-btn-primary"
                        >
                          <FiPlus /> Create order
                        </NavLink>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && totalPages > 1 && (
          <div className="qd-pagination">
            <span className="qd-muted">
              Showing {(offset - 1) * limit + 1}-{Math.min(offset * limit, totalCount)} of {totalCount}
            </span>
            <div className="qd-pages">
              <button disabled={offset <= 1} onClick={() => setOffset(offset - 1)} aria-label="Previous">
                <img src="/assets/img/arrow-left.svg" alt="" />
              </button>
              {generatePagination(offset, totalPages).map((page, idx) =>
                page === '...' ? (
                  <span key={idx} className="qd-ellipsis">...</span>
                ) : (
                  <button
                    key={idx}
                    className={offset === page ? "active" : ""}
                    onClick={() => setOffset(page)}
                  >
                    {page}
                  </button>
                )
              )}
              <button disabled={offset >= totalPages} onClick={() => setOffset(offset + 1)} aria-label="Next">
                <img src="/assets/img/arrow-right.svg" alt="" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        show={confirmation.show}
        title="Confirm delete"
        message="Do you really want to delete this user?"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        confirmText="Yes, delete"
        cancelText="Cancel"
      />
    </div>
  );
}
